import { FORM_FIELD_KINDS, RecordStringAny } from 'components/core/dynamic-form/c1FormToYupSchema'
import { Field, BoolField, StringField, TextField, PasswordField, StringSliceField, CheckboxField, ChipsField, SelectField, SelectOption } from 'pbts/c1/api/form/v1/form'
import { Struct, Value } from 'pbts/google/protobuf/struct'
import { FieldTypeOption, RequestSchemaFormFieldValues } from './RequestSchemaDrawer'

// used for converting submitted form values to proto struct
export const formToProtoStruct = (userSubmittedFields: RecordStringAny, fields?: Field[]): Struct | undefined => {
    if (Object.keys(userSubmittedFields).length === 0) {
        return undefined
    }

    function getTypedValue(fieldName: string, value: any): Value { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (!fields) {
            throw new Error('Field definitions are required to convert fields to proto')
        }
        const field = fields.find((_field) => _field.name === fieldName)
        if (!field) {
            throw new Error(`Field ${fieldName} not found in request schema`)
        }
        switch (field.type?.oneofKind) {
            case FORM_FIELD_KINDS.STRING:
                return { kind: { oneofKind: 'stringValue', stringValue: value || '' } }
            case FORM_FIELD_KINDS.BOOL:
                return { kind: { oneofKind: 'boolValue', boolValue: value } } // bool values will always have a default
            case FORM_FIELD_KINDS.STRING_SLICE:
                return { kind: { oneofKind: 'listValue', listValue: value ? { values: value.map((v) => ({ kind: { oneofKind: 'stringValue', stringValue: v } })) } : { values: [] } } }
            default:
                throw new Error(`Unknown field type: ${field.type?.oneofKind}`)
        }
    }

    return Struct.create({
        fields: Object.entries(userSubmittedFields).reduce((acc, [key, value]) => {
            acc[key] = getTypedValue(key, value)
            return acc
        }, {} as Record<string, Value>),
    })
}

// used to convert a schema create/modify details to request schema proto format
export const formFieldToProto = (field: RequestSchemaFormFieldValues['fields'][number]): Field => {
    const fieldType: FieldTypeOption = field.field.type
    let typeSpecificFields
    let isRequired = false
    if (fieldType === FieldTypeOption.Text) {
        isRequired = field.field.text.required
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.STRING,
            stringField: StringField.create({
                defaultValue: field.field.text.default_value,
                placeholder: field.field.text.placeholder,
                view: { oneofKind: 'textField', textField: TextField.create({ multiline: false }) },
            }),
        }
    } else if (fieldType === FieldTypeOption.Password) {
        isRequired = field.field.password.required
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.STRING,
            stringField: StringField.create({
                defaultValue: field.field.password.default_value,
                placeholder: field.field.password.placeholder,
                view: { oneofKind: 'passwordField', passwordField: PasswordField.create({}) },
            }),
        }
    } else if (fieldType === FieldTypeOption.Checkbox) {
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.BOOL,
            boolField: BoolField.create({
                defaultValue: field.field.checkbox.default_value,
                view: { oneofKind: 'checkboxField', checkboxField: CheckboxField.create({}) },
            }),
        }
    } else if (fieldType === FieldTypeOption.MultiStringChips) {
        isRequired = field.field.multi_string_chips.required
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.STRING_SLICE,
            stringSliceField: StringSliceField.create({
                defaultValues: field.field.multi_string_chips.default_values,
                placeholder: field.field.multi_string_chips.placeholder,
                view: { oneofKind: 'chipsField', chipsField: ChipsField.create({}) },
            }),
        }
    } else if (fieldType === FieldTypeOption.TextArea) {
        isRequired = field.field.text_area.required
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.STRING,
            stringField: StringField.create({
                defaultValue: field.field.text_area.default_value,
                placeholder: field.field.text_area.placeholder,
                view: { oneofKind: 'textField', textField: TextField.create({ multiline: true }) },
            }),
        }
    } else if (fieldType === FieldTypeOption.Select) {
        isRequired = field.field.select.required
        typeSpecificFields = {
            oneofKind: FORM_FIELD_KINDS.STRING,
            stringField: StringField.create({
                defaultValue: (field.field.select.default_value ?? '').trim(),
                placeholder: field.field.select.placeholder,
                view: {
                    oneofKind: 'selectField',
                    selectField: SelectField.create({
                        options: field.field.select.options.map((option) => SelectOption.create({
                            displayName: String(option).trim(),
                            value: String(option).trim(),
                        })),
                    }),
                },
            }),
        }
    } else {
        throw new Error(`Unknown field type: ${fieldType}`)
    }

    return Field.create({
        name: field.field.name,
        displayName: field.field.display_name,
        description: field.field.description,
        required: isRequired,
        type: typeSpecificFields,
    })
}

// used to convert a request schema proto format to the form field values for editing schemas
export const protoToFormField = (field: Field): RequestSchemaFormFieldValues['fields'][number] => {
    const commonFieldValues = {
        name: field.name,
        display_name: field.displayName,
        description: field.description,
    }
    if (field.type?.oneofKind === FORM_FIELD_KINDS.STRING) {
        const stringField = field.type?.stringField
        const view = stringField?.view?.oneofKind
        if (view === 'textField') {
            const isMultiline = stringField?.view?.textField?.multiline
            if (isMultiline) {
                return {
                    field: {
                        type: FieldTypeOption.TextArea,
                        ...commonFieldValues,
                        text_area: {
                            default_value: stringField?.defaultValue,
                            placeholder: stringField?.placeholder,
                            required: field.required,
                        },
                    },
                }
            }
            return {
                field: {
                    type: FieldTypeOption.Text,
                    ...commonFieldValues,
                    text: {
                        default_value: stringField?.defaultValue,
                        placeholder: stringField?.placeholder,
                        required: field.required,
                    },
                },
            }
        } if (view === 'passwordField') {
            return {
                field: {
                    type: FieldTypeOption.Password,
                    ...commonFieldValues,
                    password: {
                        default_value: stringField?.defaultValue,
                        placeholder: stringField?.placeholder,
                        required: field.required,
                    },
                },
            }
        } if (view === 'selectField') {
            return {
                field: {
                    type: FieldTypeOption.Select,
                    ...commonFieldValues,
                    select: {
                        options: stringField?.view?.selectField?.options?.map((option) => option.value) ?? [],
                        default_value: stringField?.defaultValue,
                        placeholder: stringField?.placeholder,
                        required: field.required,
                    },
                },
            }
        }
    } else if (field.type?.oneofKind === FORM_FIELD_KINDS.BOOL) {
        const boolField = field.type?.boolField
        return {
            field: {
                type: FieldTypeOption.Checkbox,
                ...commonFieldValues,
                checkbox: {
                    default_value: boolField?.defaultValue,
                },
            },
        }
    } else if (field.type?.oneofKind === FORM_FIELD_KINDS.STRING_SLICE) {
        const stringSliceField = field.type?.stringSliceField
        return {
            field: {
                type: FieldTypeOption.MultiStringChips,
                ...commonFieldValues,
                multi_string_chips: {
                    default_values: stringSliceField?.defaultValues,
                    placeholder: stringSliceField?.placeholder,
                    required: field.required,
                },
            },
        }
    } else {
        throw new Error(`Unknown field type: ${field.type?.oneofKind}`)
    }
}

// used to set default values to existing data if it exists, then default value from the schema, then an empty default value
export const getDefaultValues = (struct: Struct, fields?: Field[]): RecordStringAny => {
    const result: RecordStringAny = {}
    for (const field of fields ?? []) {
        const structKind = struct?.fields?.[field.name]?.kind
        switch (field.type?.oneofKind) {
            case FORM_FIELD_KINDS.STRING: {
                result[field.name] = structKind?.oneofKind === 'stringValue'
                    ? structKind.stringValue
                    : field.type?.stringField?.defaultValue ?? ''
                break
            }
            case FORM_FIELD_KINDS.BOOL: {
                result[field.name] = structKind?.oneofKind === 'boolValue'
                    ? structKind.boolValue
                    : field.type?.boolField?.defaultValue ?? false
                break
            }
            case FORM_FIELD_KINDS.STRING_SLICE:
                result[field.name] = structKind?.oneofKind === 'listValue'
                    ? structKind.listValue.values.map((v) => {
                        if (v.kind?.oneofKind === 'stringValue') return v.kind.stringValue
                        return null
                    }).filter(Boolean)
                    : field.type?.stringSliceField?.defaultValues ?? []
                break
            default:
                result[field.name] = null
        }
    }
    return result
}
